import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { activityService } from '../../services';
import type { ActivityEntityType } from '../../services/entityRoutes';
import { useActivityRefreshStore } from '../../store';
import { buildPills, PILL_GROUPS, type PillView } from '../../lib/kindMap';
import {
  feedReducer,
  INITIAL_FEED_STATE,
  selectFooterCounts,
} from '../../lib/feedReducer';
import { hasNewRow, highWaterMark, nextDelayMs, type FeedMark } from '../../lib/arrivalPoll';
import {
  applyCollapse,
  applyRailClick,
  readPanelPrefs,
  writePanelPrefs,
  type PanelPrefs,
  type PanelSection,
} from '../../lib/panelPrefs';
import { ActivityFeed } from './ActivityFeed';
import { ActivityFilterPills } from './ActivityFilterPills';
import { ActivityPanelHeader } from './ActivityPanelHeader';
import { ActivitySectionRail } from './ActivitySectionRail';
import { CommentsComingSoon } from './CommentsComingSoon';
import { useSectionHotkeys } from './useSectionHotkeys';

/** Fixed, per the design. A named constant + inline style rather than `w-[364px]`: an arbitrary Tailwind
 *  value counts against the UI scan, and this is a real fixed dimension rather than consistency debt. */
const PANEL_WIDTH_PX = 364;

const PAGE_SIZE = 25;

interface ActivityDockProps {
  /** The union, not `string` — an unmapped record is a build error, never a blank panel at runtime. */
  entityType: ActivityEntityType;
  entityId: string | number;
}

/**
 * The rail + panel, and the only stateful piece of this feature.
 *
 * Everything it decides lives in tested pure functions (`feedReducer`, `arrivalPoll`, `panelPrefs`,
 * `kindMap`, `phtDay`) because this repo's vitest runs in Node with no DOM — there is no jsdom and no
 * testing-library, so a component cannot be tested at all. Keeping this file thin is what makes the feature
 * testable rather than merely tested-adjacent.
 */
/**
 * A record change REMOUNTS the panel, and that is the fix for a real bug rather than a stylistic choice.
 *
 * ⚠️ There used to be a `reset` action dispatched from an effect when `entityId` changed. On mount both
 * effects ran in declaration order: reset dispatched first and advanced the generation counter, then the
 * fetch effect fired having already captured the PREVIOUS generation from a ref during render — so the
 * response came back stamped with a generation the reducer no longer recognised and was dropped by the
 * stale-response guard. **The panel loaded nothing until you switched sections and back**, which flipped
 * the fetch gate and re-ran it with the corrected number. The same race hit navigation between two
 * picklists, so guarding only the first mount would have moved the bug rather than removed it.
 *
 * Keying on the record makes the whole class unrepresentable: a different record is a different component
 * instance with a fresh `INITIAL_FEED_STATE`, so there is no reset to order against and no window in which
 * a captured generation can go stale. The generation counter now serves only the purpose it was designed
 * for — dropping a response that belongs to an abandoned FILTER chain.
 *
 * Panel prefs survive the remount because they are read from localStorage, not from React state.
 */
export function ActivityDock({ entityType, entityId }: ActivityDockProps) {
  return <ActivityDockInner key={`${entityType}:${entityId}`} entityType={entityType} entityId={entityId} />;
}

function ActivityDockInner({ entityType, entityId }: ActivityDockProps) {
  const [prefs, setPrefs] = useState<PanelPrefs>(readPanelPrefs);
  const [state, dispatch] = useReducer(feedReducer, INITIAL_FEED_STATE);

  // Re-labels "Today"/"Yesterday" across midnight. A panel docked on a warehouse floor stays open for
  // shifts; without this the sticky headers freeze at whatever the clock said when it mounted.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  // ⚠️ Subscribed as a VALUE, never as a getter called in render. The React Compiler memoizes an expression
  // on a stable function reference, so `useStore().fn()` in render serves stale UI even while the component
  // re-renders — the documented 86d3d1z40 failure.
  const refreshVersion = useActivityRefreshStore((s) => s.version);

  const updatePrefs = useCallback((next: PanelPrefs) => setPrefs(writePanelPrefs(next)), []);

  const onRailSelect = useCallback(
    (section: PanelSection) => updatePrefs(applyRailClick(prefs, section)),
    [prefs, updatePrefs],
  );
  const onCollapse = useCallback(() => updatePrefs(applyCollapse(prefs)), [prefs, updatePrefs]);
  const onToggleActivity = useCallback(
    () => updatePrefs(applyRailClick(prefs, 'activity')),
    [prefs, updatePrefs],
  );

  useSectionHotkeys({ onToggleActivity, onCollapse });

  // ── Fetching ──────────────────────────────────────────────────────────────────────────────────
  //
  // The generation guard lives in the reducer; this ref only carries the CURRENT generation into the
  // async callback, which cannot read state. A ref is safe here because it is never read during render.
  const generation = useRef(state.generation);
  generation.current = state.generation;

  const selectedKinds = PILL_GROUPS.find((g) => g.id === state.pill)?.kinds ?? null;
  const kindsKey = selectedKinds ? selectedKinds.join(',') : '';

  const fetchPage = useCallback(
    async (append: boolean) => {
      const gen = generation.current;
      dispatch(append ? { type: 'more-requested' } : { type: 'first-page-requested' });

      try {
        const page = await activityService.getForEntity(entityType, entityId, {
          pageSize: PAGE_SIZE,
          ...(append && state.cursor
            ? { cursorOccurredAtUtc: state.cursor.occurredAtUtc, cursorId: state.cursor.id }
            : {}),
          ...(kindsKey ? { kinds: kindsKey.split(',') as never } : {}),
        });
        dispatch({ type: 'page-received', page, append, generation: gen });
      } catch (e) {
        dispatch({
          type: 'request-failed',
          message: e instanceof Error ? e.message : 'Failed to load activity',
          generation: gen,
          append,
        });
      }
    },
    [entityType, entityId, kindsKey, state.cursor],
  );

  // Page 1, on open / filter change / refresh bump. Deliberately NOT keyed on `fetchPage`, whose identity
  // changes with the cursor — that would refetch page 1 every time a "show more" landed. A RECORD change
  // does not appear here either: it remounts this component (see ActivityDock above).
  //
  // ⚠️ Gated on `prefs.open`: below `lg` the panel is also hidden by CSS, and a mount that fetched anyway
  // would issue an invisible request on every phone load of a picklist. Fetching when opened is both
  // cheaper and honest.
  const shouldFetch = prefs.open && prefs.section === 'activity';
  useEffect(() => {
    if (!shouldFetch) return;
    void fetchPage(false);
    // fetchPage's identity tracks the cursor; including it would turn every append into a page-1 refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, kindsKey, refreshVersion]);

  // The quiet re-read (owner 2026-10-08): the page's 30-second tick and tab return. No arrival poll, and none while a
  // page-1 read is already out — that read brings the same rows. A ref, because the check runs in the effect, never
  // in render.
  const pulse = useActivityRefreshStore((s) => s.pulse);
  const loadingFirst = useRef(false);
  loadingFirst.current = state.loading.first;
  useEffect(() => {
    if (pulse === 0 || !shouldFetch || loadingFirst.current) return;
    void fetchPage(false);
    // Keyed on the pulse alone: the open/filter effect above owns every other reason to read page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse]);

  // ── The confirm-poll (D-3) ────────────────────────────────────────────────────────────────────
  //
  // A mutation's activity row is written LATER by an outbox consumer, so the refetch that `bump()` triggers
  // usually returns WITHOUT it. This polls on a bounded schedule until a row appears that was not already
  // on screen — then stops. At most four extra requests over ~10s, and only ONE schedule outstanding: a
  // 39-pick burst bumps 39 times, and stacked polls would turn the panel into a request storm.
  const [seen, setSeen] = useState<FeedMark | null>(null);
  const [attempt, setAttempt] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  // The mark is taken when the bump arrives — what was on screen BEFORE the mutation's row could land.
  const itemsRef = useRef(state.items);
  itemsRef.current = state.items;

  useEffect(() => {
    if (refreshVersion === 0 || !shouldFetch) return;
    setSeen(highWaterMark(itemsRef.current));
    setAttempt(0);
    setTimedOut(false);
  }, [refreshVersion, shouldFetch]);

  const awaitingArrival = attempt !== null;
  // A quiet bump (posting) polls the same way but shows no placeholder and no "Still recording" (owner 2026-10-08).
  const quietBump = useActivityRefreshStore((s) => s.quiet);

  useEffect(() => {
    if (attempt === null) return;

    // The row landed — stop, whatever is left of the budget.
    if (hasNewRow(seen, state.items)) {
      setAttempt(null);
      return;
    }

    const delay = nextDelayMs(attempt);
    if (delay === null) {
      setAttempt(null);
      setTimedOut(true);
      return;
    }

    const t = setTimeout(() => {
      void fetchPage(false);
      setAttempt((a) => (a === null ? null : a + 1));
    }, delay);

    return () => clearTimeout(t);
    // fetchPage's identity tracks the cursor; rescheduling on it restarts the backoff on every append.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, seen, state.items]);

  // ── Render ────────────────────────────────────────────────────────────────────────────────────

  const pills = buildPills(state.kindCounts);
  const footer = selectFooterCounts(state);

  const onPillSelect = useCallback((pill: PillView) => {
    dispatch({ type: 'filter-selected', pill: pill.id });
  }, []);

  return (
    // ONE provider for the whole dock. There is no app-wide TooltipProvider, and a <Tooltip> without an
    // ancestor provider THROWS — which on a detail page means the record itself stops rendering.
    <TooltipProvider delayDuration={150}>
      <div className="flex h-full">
        {prefs.open && (
          <aside
            aria-label={prefs.section === 'activity' ? 'Activity' : 'Comments'}
            style={{ width: PANEL_WIDTH_PX }}
            className={[
              'flex h-full shrink-0 flex-col border-l border-border bg-card',
              // Hand-written keyframe, NOT `animate-in slide-in-from-right`: tailwindcss-animate is not
              // installed in this project, so every one of those classes compiles to nothing.
              'activity-panel-enter',
              // Below the threshold the panel OVERLAYS instead of pushing — there is not 364px of spare
              // width on a tablet, and the alternative (hiding it entirely, as the old rail did) means
              // the feature simply does not exist there.
              //
              // M8: keyed to the CONTENT COLUMN, not the viewport. `lg:static` believed it had 1366px
              // even when a shell panel had already taken the column down to 938 — so the dock pushed
              // when it should have overlaid, and the record beside it lost another 364px. Same 1024px
              // threshold, measured where it matters. See .shell-cq-* in globals.css.
              'shell-cq-dock',
            ].join(' ')}
          >
            <ActivityPanelHeader
              section={prefs.section}
              count={state.totalCount}
              onCollapse={onCollapse}
            />

            {prefs.section === 'activity' ? (
              <>
                <ActivityFilterPills pills={pills} selected={state.pill} onSelect={onPillSelect} />

                {/* `overflow-x-hidden` is a stated backstop, not a licence to stop wrapping (§9). Per
                    CSS, `overflow-y-auto` alone computes overflow-x to `auto`, so ONE over-wide nowrap
                    child gives the whole feed a horizontal scrollbar — observed live. ⚠️ `hidden` CLIPS
                    rather than scrolls: any child that can exceed 364px still needs `min-w-0` plus its
                    own wrapping/breaking rule, or its content becomes unreachable. */}
                <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
                  <ActivityFeed
                    state={state}
                    awaitingArrival={awaitingArrival && !quietBump}
                    arrivalTimedOut={timedOut && !quietBump}
                    onLoadMore={() => void fetchPage(true)}
                    onRetry={() => void fetchPage(false)}
                    now={now}
                  />
                </div>

                <footer className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
                  {/* Verbatim from the handoff §6. No Export button — dropped, not approximated (G5). */}
                  Showing <span className="tabular-nums">{footer.shown}</span> of{' '}
                  <span className="tabular-nums">{footer.total ?? footer.shown}</span> · server-stamped
                </footer>
              </>
            ) : (
              <div className="min-w-0 flex-1 overflow-y-auto">
                <CommentsComingSoon />
              </div>
            )}
          </aside>
        )}

        <ActivitySectionRail active={prefs.open ? prefs.section : null} onSelect={onRailSelect} />
      </div>
    </TooltipProvider>
  );
}
